/**
 * A note a manager keeps on an employee: one entity for the "Notes et rétroaction" tab and the
 * "Croissance" tab alike, filed under a category (`Career` for the growth journey). Shapes mirror
 * `EmployeeNoteCreateRequest`, `EmployeeNoteUpdateRequest` and `EmployeeNoteResponse` of the performance API, routes under
 * `/performance-profile/{employeeId}/notes`. Who may read a note follows the hierarchy on the
 * employee, never the author: there is no note private to whoever wrote it. Who may change one is
 * the server's call, sent with every note (`canEdit`, `canDelete`).
 */

export const EMPLOYEE_NOTE_CATEGORIES = [
    'General',
    'Performance',
    'Personal',
    'Collaboration',
    'Career',
] as const;

export type EmployeeNoteCategory = (typeof EMPLOYEE_NOTE_CATEGORIES)[number];

/** The note category the growth journey files under. */
export const CAREER_NOTE_CATEGORY = 'Career' satisfies EmployeeNoteCategory;

/**
 * The folder a note written under a manager assessment question starts in: the employee's
 * performance and results (Maxime, 2026-09-13). Its author may file it elsewhere afterwards; where
 * it came from is its link, not its folder.
 */
export const ASSESSMENT_NOTE_DEFAULT_CATEGORY = 'Performance' satisfies EmployeeNoteCategory;

/**
 * The categories the "Notes" tab lets a manager file a note under: every one of them. The profile
 * is where the employee's notes come together, so a career note can be written here as well as
 * from the growth journey (Maxime, 2026-09-12).
 */
export const NOTES_TAB_CATEGORIES: readonly EmployeeNoteCategory[] = EMPLOYEE_NOTE_CATEGORIES;

export interface EmployeeNote {
    id: string;
    employeeId: string;
    /** Directory id of the author; `null` for rows written before blame tracking. */
    authorId: string | null;
    category: EmployeeNoteCategory;
    /** HTML, sanitized by the server before it is stored; authored with the library's `RichText`. */
    content: string;
    /**
     * The meeting the note was written in, when it came from one: the card links back to its plan.
     * `null` for a note written straight from a profile tab. Read at creation and kept by the
     * server on a replacement, so editing a note never moves its origin.
     */
    thematicMeetingId: string | null;
    /** The meeting of `thematicMeetingId` has been deleted since: the note keeps its origin, but
     * there is no meeting left to link back to. */
    isThematicMeetingDeleted: boolean;
    /** The manager assessment the note was written during, when it was: the card links back to it. */
    cycleInstanceId: string | null;
    /** The assessment question the note was written under; one note per question. */
    reviewQuestionId: string | null;
    /** Only the author may edit a note. */
    canEdit: boolean;
    /** The author may delete a note, and so may someone who manages every department's performance. */
    canDelete: boolean;
    createdAt: string;
    updatedAt: string;
}

/** A note's full replacement: what it says and where it is filed. Where it came from never changes. */
export interface EmployeeNoteUpdateRequest {
    category: EmployeeNoteCategory;
    content: string;
}

/** A new note, with where it was written, if anywhere. */
export interface EmployeeNoteCreateRequest extends EmployeeNoteUpdateRequest {
    /** Set by the writers that take a note inside a meeting plan; the notes tabs leave it out. */
    thematicMeetingId?: string | null;
    /** Set by the note box under a manager assessment question, with the question itself. */
    cycleInstanceId?: string | null;
    reviewQuestionId?: string | null;
}

/**
 * Narrows a read to the notes written in one meeting or during one manager assessment; the
 * profile tabs pass neither, so they never filter on where a note came from.
 */
export interface EmployeeNoteOriginFilter {
    thematicMeetingId?: string;
    cycleInstanceId?: string;
}

/** What a composer hands back: the text and the folder it goes in. */
export interface EmployeeNoteDraft {
    category: EmployeeNoteCategory;
    content: string;
}

/** The "Notes" tab pages by ten, like the prototype; the growth journey shows five. */
export const EMPLOYEE_NOTES_PAGE_SIZE = 10;
